import React, { useEffect, useState, useRef } from 'react';
import { XIcon, EyeIcon, EyeOffIcon, CheckIcon, EditIcon, CheckCircleIcon } from 'lucide-react';
interface AuthBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: (isNewUser: boolean) => void;
}
export function AuthBottomSheet({
  isOpen,
  onClose,
  onAuthSuccess
}: AuthBottomSheetProps) {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [otpMethod, setOtpMethod] = useState<'whatsapp' | 'sms'>('whatsapp');
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [loginPhone, setLoginPhone] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [registerPhone, setRegisterPhone] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpInputs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    if (showOTP && resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
  }, [showOTP, resendTimer]);
  useEffect(() => {
    if (!isOpen) {
      setShowOTP(false);
      setShowSuccess(false);
      setOtp(['', '', '', '', '', '']);
      setResendTimer(60);
      setCanResend(false);
    }
  }, [isOpen]);
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      otpInputs.current[index + 1]?.focus();
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputs.current[index - 1]?.focus();
    }
  };
  const handleLogin = () => {
    setShowOTP(true);
    setResendTimer(60);
    setCanResend(false);
  };
  const handleRegister = () => {
    setShowOTP(true);
    setResendTimer(60);
    setCanResend(false);
  };
  const handleContinueWithOTP = () => {
    setShowOTP(true);
    setResendTimer(60);
    setCanResend(false);
  };
  const handleVerifyOTP = () => {
    setShowSuccess(true);
    setTimeout(() => {
      onAuthSuccess(activeTab === 'register');
    }, 2000);
  };
  const handleResendOTP = (method: 'whatsapp' | 'sms') => {
    setOtpMethod(method);
    setResendTimer(60);
    setCanResend(false);
    setOtp(['', '', '', '', '', '']);
    otpInputs.current[0]?.focus();
  };
  const handleEditPhone = () => {
    setShowOTP(false);
    setOtp(['', '', '', '', '', '']);
  };
  const currentPhone = activeTab === 'login' ? loginPhone : registerPhone;
  if (!isOpen) return null;
  if (showSuccess) {
    return <>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300" style={{
        backdropFilter: 'blur(4px)'
      }} data-id="element-3756" data-mp="21gatiegqu2gb" />
        <div className="fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-3xl shadow-2xl transition-transform duration-300 ease-out" style={{
        maxHeight: '90vh',
        height: 'auto',
        maxWidth: '640px',
        margin: '0 auto'
      }} data-id="element-3757" data-mp="1wjke7tcrjavy">
          <div className="flex justify-center pt-3 pb-2" data-id="element-3758" data-mp="2f7z58gn2yowr">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full" data-id="element-3759" data-mp="2f8hag2rq0dgt" />
          </div>
          <div className="px-6 py-12 text-center" data-id="element-3760" data-mp="2t89wvlw7s7iz">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3761" data-mp="2p5vgr9jxjlve">
              <CheckCircleIcon className="w-12 h-12 text-green-600" data-id="element-3762" data-mp="mjq24g1woym6" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3763" data-mp="rib0e9xll87z">
              {activeTab === 'register' ? 'Account Created!' : 'Welcome Back!'}
            </h2>
            <p className="text-gray-600" data-id="element-3764" data-mp="w9t3p4n3fkw4">
              {activeTab === 'register' ? 'Your account has been successfully created' : 'You have successfully logged in'}
            </p>
          </div>
        </div>
      </>;
  }
  if (showOTP) {
    return <>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300" onClick={onClose} style={{
        backdropFilter: 'blur(4px)'
      }} data-id="element-3765" data-mp="8jp0gydthn5n" />
        <div className="fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-3xl shadow-2xl transition-transform duration-300 ease-out" style={{
        maxHeight: '90vh',
        height: 'auto',
        maxWidth: '640px',
        margin: '0 auto'
      }} data-id="element-3766" data-mp="hhse78ik9fmw">
          <div className="flex justify-center pt-3 pb-2" data-id="element-3767" data-mp="39fzgtu6rwoqw">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full" data-id="element-3768" data-mp="39gizxe85tj6i" />
          </div>
          <div className="sticky top-0 bg-white px-6 pt-4 pb-4 rounded-t-3xl z-10" data-id="element-3769" data-mp="34npu0n0yg4su">
            <div className="flex items-center justify-between" data-id="element-3770" data-mp="2zov2mwox97x4">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-3771" data-mp="2uqtl4g759gnr">
                Verify your mobile number
              </h2>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-3772" data-mp="2prgy9vg9w9l6">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-3773" data-mp="2ls99fz004791" />
              </button>
            </div>
          </div>
          <div className="px-6 pb-6" data-id="element-3774" data-mp="jys26wfs98et">
            <p className="text-sm text-gray-600 text-center mb-2" data-id="element-3775" data-mp="jxqqcpqy0hw1">
              Enter the 6-digit OTP sent to
            </p>
            <div className="flex items-center justify-center gap-2 mb-4" data-id="element-3776" data-mp="f08ht0gu84ok">
              <span className="text-base font-medium text-gray-900" data-id="element-3777" data-mp="y3951yufg91a">
                +966 {currentPhone}
              </span>
              <button onClick={handleEditPhone} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-3778" data-mp="12wbqtksyqe6x">
                <EditIcon className="w-4 h-4 text-[#1E2B55]" data-id="element-3779" data-mp="u271j5th9ati" />
              </button>
            </div>
            <div className="flex items-center justify-center gap-1 mb-6" data-id="element-3780" data-mp="1hs1fxrtg37fv">
              <span className="text-sm text-gray-600" data-id="element-3781" data-mp="1ltwjl2yulwwt">via</span>
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24" data-id="element-3782" data-mp="1lt3hwgeqxxlj">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-3783" data-mp="1csf4nnx6g19p" />
              </svg>
              <span className="text-sm font-medium text-green-600" data-id="element-3784" data-mp="2njcsug1kul15">
                WhatsApp
              </span>
            </div>
            <div className="flex justify-center gap-2 mb-6" data-id="element-3785" data-mp="1y0sumbp8ptvl">
              {otp.map((digit, index) => <input key={index} ref={el => otpInputs.current[index] = el} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(index, e.target.value)} onKeyDown={e => handleOtpKeyDown(index, e)} className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-[#1E2B55] focus:outline-none transition-colors" data-id="element-3786" data-mp="221l2b0igrqpr" data-mpi={index} />)}
            </div>
            <div className="text-center mb-6" data-id="element-3787" data-mp="2ur1pnjr88hf4">
              <p className="text-sm text-gray-600 mb-3" data-id="element-3788" data-mp="2uswcj3m26jlu">
                Didn't receive the OTP?
              </p>
              {canResend ? <div className="flex gap-3 justify-center" data-id="element-3789" data-mp="34og3rcpogu49">
                  <button onClick={() => handleResendOTP('whatsapp')} className="flex items-center gap-2 px-6 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors" data-id="element-3790" data-mp="34mlke2u21a2a">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" data-id="element-3791" data-mp="j4hr9vbw0n4i">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-3792" data-mp="1brub3xqnygu4" />
                    </svg>
                    WhatsApp
                  </button>
                  <button onClick={() => handleResendOTP('sms')} className="flex items-center gap-2 px-6 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors" data-id="element-3793" data-mp="5czvfknkrx7y">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-3794" data-mp="f8tg5rbg6x0t">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" data-id="element-3795" data-mp="tvs4veix7dbu" />
                    </svg>
                    SMS
                  </button>
                </div> : <p className="text-sm text-gray-500" data-id="element-3796" data-mp="f0lvchkm8wdr">
                  Resend OTP in{' '}
                  <span className="font-bold text-[#1E2B55]" data-id="element-3797" data-mp="19h1jw3xiybc">
                    {resendTimer}s
                  </span>
                </p>}
            </div>
            <button onClick={handleVerifyOTP} disabled={otp.some(digit => !digit)} className="w-full py-3 bg-[#1E2B55] text-white font-bold rounded-lg hover:bg-[#162041] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4" data-id="element-3798" data-mp="3ng5l36m2vwo2">
              Confirm
            </button>
            <p className="text-xs text-gray-500 text-center" data-id="element-3799" data-mp="34ivmu6xuv026">
              By continuing, you agree to share your contact details with Innova
              for service purposes and accept their{' '}
              <a href="#" className="text-[#1E2B55] underline" data-id="element-3800" data-mp="2umsak89ahl1i">
                terms
              </a>{' '}
              and{' '}
              <a href="#" className="text-[#1E2B55] underline" data-id="element-3801" data-mp="3s8pxchupccj9">
                policies
              </a>
            </p>
          </div>
        </div>
      </>;
  }
  return <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300" onClick={onClose} style={{
      backdropFilter: 'blur(4px)'
    }} data-id="element-3802" data-mp="3i9qph1hrgkxb" />
      <div className="fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-3xl shadow-2xl transition-transform duration-300 ease-out" style={{
      maxHeight: '90vh',
      height: 'auto',
      maxWidth: '640px',
      margin: '0 auto',
      transform: isOpen ? 'translateY(0)' : 'translateY(100%)'
    }} data-id="element-3803" data-mp="26xg6tnwqe56l">
        <div className="flex justify-center pt-3 pb-2" data-id="element-3804" data-mp="rcxgcrjpg5a5">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" data-id="element-3805" data-mp="rbdvenn3t621" />
        </div>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 pt-4 pb-0 rounded-t-3xl z-10" data-id="element-3806" data-mp="w58eppysrj0l">
          <div className="flex items-center justify-between mb-4" data-id="element-3807" data-mp="115323jjd5qgw">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-3808" data-mp="153akeith6zu7">
              {activeTab === 'login' ? 'Sign In' : 'Create Account'}
            </h2>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-3809" data-mp="1f2roivt2x5o2">
              <XIcon className="w-5 h-5 text-gray-500" data-id="element-3810" data-mp="3bw36upyeww3w" />
            </button>
          </div>
          <div className="flex bg-gray-100 rounded-lg p-1" data-id="element-3811" data-mp="3gv7hll0zj1wc">
            <button onClick={() => setActiveTab('login')} className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${activeTab === 'login' ? 'bg-white text-[#1E2B55] shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3812" data-mp="1b5vqgbe8vf2a">
              Login
            </button>
            <button onClick={() => setActiveTab('register')} className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${activeTab === 'register' ? 'bg-white text-[#1E2B55] shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3813" data-mp="1g58cwv17ek4c">
              Create Account
            </button>
          </div>
        </div>
        <div className="overflow-y-auto px-6 py-6" style={{
        maxHeight: 'calc(90vh - 240px)'
      }} data-id="element-3814" data-mp="118zetwt1ucti">
          {activeTab === 'login' ? <div className="space-y-4" data-id="element-3815" data-mp="8q2glue3wl45">
              <div data-id="element-3816" data-mp="4ps2fug5uoe7">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3817" data-mp="4qbkrc9q3esr">
                  Phone Number
                </label>
                <div className="flex" data-id="element-3818" data-mp="3qpocjyrst1l4">
                  <div className="flex items-center px-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg" data-id="element-3819" data-mp="3vhzbozepsjcm">
                    <span className="text-sm text-gray-700" data-id="element-3820" data-mp="3gtlccygibd0t">+966</span>
                  </div>
                  <input type="tel" value={loginPhone} onChange={e => setLoginPhone(e.target.value)} placeholder="5xxxxxxxx" className="flex-1 px-4 py-3 border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-3821" data-mp="3lrwjh1ff39fc" />
                </div>
              </div>
              <div data-id="element-3822" data-mp="8c8fyu4flsii">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3823" data-mp="8boxncavd23y">
                  Password
                </label>
                <div className="relative" data-id="element-3824" data-mp="2t3h7yovhv98r">
                  <input type={showPassword ? 'text' : 'password'} value={loginPassword} onChange={e => setLoginPassword(e.target.value)} placeholder="Enter your password" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent pr-12" data-id="element-3825" data-mp="2t4jvqnxisvkg" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700" data-id="element-3826" data-mp="ktrh0nzge9of">
                    {showPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-3827" data-mp="6xximywb32pk" /> : <EyeIcon className="w-5 h-5" data-id="element-3828" data-mp="1yuh3ofjbemc" />}
                  </button>
                </div>
              </div>
              <div className="text-right" data-id="element-3829" data-mp="1waiwtstlbl7s">
                <button className="text-sm text-[#1E2B55] hover:underline font-medium" data-id="element-3830" data-mp="1wbk7hw6p25oz">
                  Forgot password?
                </button>
              </div>
              <div className="relative" data-id="element-3831" data-mp="28sv1wfw6sds5">
                <div className="absolute inset-0 flex items-center" data-id="element-3832" data-mp="28rtr8cj31tay">
                  <div className="w-full border-t border-gray-200" data-id="element-3833" data-mp="2dlef3bqyp6wf" />
                </div>
                <div className="relative flex justify-center text-sm" data-id="element-3834" data-mp="2ijpqvs0thrha">
                  <span className="px-2 bg-white text-gray-500" data-id="element-3835" data-mp="1kvxomjmwk02u">Or</span>
                </div>
              </div>
              <button onClick={handleContinueWithOTP} className="w-full py-3 px-4 border-2 border-[#1E2B55] text-[#1E2B55] font-medium rounded-lg hover:bg-blue-50 transition-colors" data-id="element-3836" data-mp="1pu900ysuiin6">
                Continue with OTP
              </button>
            </div> : <div className="space-y-4" data-id="element-3837" data-mp="367z6udbcnl0i">
              <div data-id="element-3838" data-mp="36b4xhwzmmuyc">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3839" data-mp="3b7v9a84ef4dy">
                  Full Name
                </label>
                <input type="text" value={registerName} onChange={e => setRegisterName(e.target.value)} placeholder="Enter your full name" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-3840" data-mp="7tdog467d4r" />
              </div>
              <div data-id="element-3841" data-mp="ivybz82265w6">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3842" data-mp="ivganx0jh1he">
                  Phone Number
                </label>
                <div className="flex" data-id="element-3843" data-mp="sv755yp3nghh">
                  <div className="flex items-center px-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg" data-id="element-3844" data-mp="su4ftv7fdm3o">
                    <span className="text-sm text-gray-700" data-id="element-3845" data-mp="nx5k5884573d">+966</span>
                  </div>
                  <input type="tel" value={registerPhone} onChange={e => setRegisterPhone(e.target.value)} placeholder="5xxxxxxxx" className="flex-1 px-4 py-3 border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-3846" data-mp="2unc0lk5t4vwy" />
                </div>
              </div>
              <div data-id="element-3847" data-mp="10eyi79mlkvoh">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3848" data-mp="10eggvyl2vr9p">
                  Email <span className="text-gray-400" data-id="element-3849" data-mp="mfgnyeqj8q7q">(Optional)</span>
                </label>
                <input type="email" value={registerEmail} onChange={e => setRegisterEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-3850" data-mp="rea1261wqf7d" />
              </div>
              <div data-id="element-3851" data-mp="2btv8emuipqxf">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3852" data-mp="2budfyfmmrrkn">
                  Password
                </label>
                <div className="relative" data-id="element-3853" data-mp="3fd0672v4ykjl">
                  <input type={showPassword ? 'text' : 'password'} value={registerPassword} onChange={e => setRegisterPassword(e.target.value)} placeholder="Create a password" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent pr-12" data-id="element-3854" data-mp="3k4rokq451jyn" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700" data-id="element-3855" data-mp="6xy970h4ngcg">
                    {showPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-3856" data-mp="2dncycds9btlk" /> : <EyeIcon className="w-5 h-5" data-id="element-3857" data-mp="2iotvby7mevwv" />}
                  </button>
                </div>
              </div>
              <div className="flex items-start" data-id="element-3858" data-mp="2b6ch5eosusxk">
                <button onClick={() => setAcceptTerms(!acceptTerms)} className={`flex-shrink-0 w-5 h-5 flex items-center justify-center border-2 rounded ${acceptTerms ? 'bg-[#1E2B55] border-[#1E2B55]' : 'border-gray-300'}`} data-id="element-3859" data-mp="264de65wz1y15">
                  {acceptTerms && <CheckIcon className="w-3 h-3 text-white" data-id="element-3860" data-mp="1rfqwhxjvgu6r" />}
                </button>
                <label className="ml-3 text-sm text-gray-600" data-id="element-3861" data-mp="1nfgsgqfn4duj">
                  I agree to the{' '}
                  <a href="#" className="text-[#1E2B55] hover:underline font-medium" data-id="element-3862" data-mp="216lm9bwbubwy">
                    Terms & Conditions
                  </a>{' '}
                  and{' '}
                  <a href="#" className="text-[#1E2B55] hover:underline font-medium" data-id="element-3863" data-mp="18og7jlxpirhi">
                    Privacy Policy
                  </a>
                </label>
              </div>
            </div>}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4" data-id="element-3864" data-mp="38qzc7agmtpch">
          <button onClick={activeTab === 'login' ? handleLogin : handleRegister} disabled={activeTab === 'register' && !acceptTerms} className="w-full py-3 px-6 bg-[#1E2B55] text-white font-bold rounded-lg hover:bg-[#162041] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3865" data-mp="3dk1wst3flogj">
            {activeTab === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </div>
      </div>
    </>;
}